import * as React from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { toast } from '@sales-center/components'
import { Formik } from 'formik'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useGetTaxRatesOnMount } from '@user/store/serviceTax/hooks/useGetTaxRatesOnMount'
import { updateLeadTaxCode } from '@user/store/lead'
import { updateOpportunityTaxCode } from '@user/store/opportunity'
import { ENTITY_TYPES } from '@sales-center/common'
import { IServiceTax, IServiceTaxUpdate } from '@user/store/serviceTax/typings'
import { TaxCodePopoverForm } from './TaxCodePopoverForm'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Popover from '@mui/material/Popover'

const getStyles = (theme: Theme) => ({
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    padding: `${theme.spacing(1.1)} ${theme.spacing(2)}`,
    textTransform: 'uppercase',
    fontFamily: 'Source Sans Pro Bold',
  },

  container: {
    width: '310px',
    padding: theme.spacing(1),
  },

  popover: {
    pointerEvents: 'none',
  },

  popoverContent: {
    pointerEvents: 'auto',
    padding: theme.spacing(1),
  },

  loader: {
    margin: `${theme.spacing(0.4)} auto auto ${theme.spacing(1)}`,
  },

  editButton: {
    color: '#005AA3',
    paddingLeft: theme.spacing(0.5),
  },
})

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  locationId: number
  taxCode: string
  canadianLocation: boolean
  onOverride?: (t: IServiceTax) => void
}

interface FormValues {
  taxCode: string
}

export const TaxCodePopover: FC<Props> = (props) => {
  const {
    entityId,
    entityType,
    locationId,
    taxCode,
    onOverride,
    canadianLocation,
  } = props
  const dispatch = useAppDispatch()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? 'tax-rate-popover' : undefined

  const onClose = useCallback(() => setAnchorEl(null), [])
  const onOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }, [])

  const { taxRateStatus, taxRates } = useGetTaxRatesOnMount(
    canadianLocation,
    taxCode,
  )
  const loading = taxRateStatus === 'pending'

  const onSave = useCallback(
    async (values: FormValues) => {
      const taxCodeId = taxRates?.find((e) => e.name === values.taxCode)?.id

      const model: IServiceTaxUpdate = {
        entityId: entityId,
        locationId: locationId,
        taxRateId: +taxCodeId!,
      }

      const response =
        entityType === 'LEAD'
          ? await dispatch(updateLeadTaxCode(model))
          : await dispatch(updateOpportunityTaxCode(model))

      if (
        updateLeadTaxCode.fulfilled.match(response) ||
        updateOpportunityTaxCode.fulfilled.match(response)
      ) {
        toast.success('Tax Rate Updated.')

        if (response.payload.draftAgreementsUpdated) {
          toast.success(
            'Service information has been updated on any draft agreements.',
          )
        }

        if (onOverride) {
          onOverride(response.payload)
        }

        onClose()
      } else {
        toast.error('An Error occurred: ' + response.payload?.Message)
      }
    },
    [entityId, entityType, locationId, onClose, onOverride, taxRates, dispatch],
  )

  useEffect(() => {
    if (taxRateStatus === 'fail') {
      toast.error(
        'An Error occurred.  We are unable to load Tax Rates at this time.',
      )
    }
  }, [taxRateStatus])
  const styles = getStyles(useTheme())
  return (
    <>
      <Link
        component="button"
        onClick={onOpen}
        sx={styles.editButton}
        data-test-id={`service-location-details-popover-edit-button-${locationId}`}
      >
        Edit
      </Link>
      <Popover
        id={id}
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{ elevation: 3 }}
      >
        <Box sx={styles.header}>
          <span>Edit Tax Code</span>
        </Box>

        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexGrow={1}
            sx={styles.container}
          >
            <CircularProgress size="30px" sx={styles.loader} />
          </Box>
        )}

        {taxRateStatus === 'success' && (
          <Box p={2} sx={styles.container}>
            <Grid item container sx={styles.loader}>
              <Formik initialValues={{ taxCode: taxCode }} onSubmit={onSave}>
                <TaxCodePopoverForm taxRates={taxRates} onClose={onClose} />
              </Formik>
            </Grid>
          </Box>
        )}
      </Popover>
    </>
  )
}

TaxCodePopover.displayName = 'TaxCodePopover'
